import React from 'react';
import AudioPlayer from '@/pages/workflow/component/audio-player';
import {
  TranscriptViewerAudio,
  TranscriptViewerContainer,
  TranscriptViewerPlayPauseButton,
  TranscriptViewerScrubBar,
  TranscriptViewerWords,
  type CharacterAlignmentResponseModel,
} from '@/components/ui/transcript-viewer';

function isCharacterAlignment(
  value: unknown,
): value is CharacterAlignmentResponseModel {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    Array.isArray(v.characters) &&
    Array.isArray(v.characterStartTimesSeconds) &&
    Array.isArray(v.characterEndTimesSeconds) &&
    v.characters.length > 0 &&
    v.characters.length === v.characterStartTimesSeconds.length &&
    v.characters.length === v.characterEndTimesSeconds.length
  );
}

/** Pull ElevenLabs character alignment out of node result metadata. */
export function extractAudioAlignment(
  result: Record<string, unknown> | null | undefined,
): CharacterAlignmentResponseModel | null {
  if (!result || typeof result !== 'object') return null;
  const metadata = result.metadata;
  if (!metadata || typeof metadata !== 'object') return null;
  const alignment = (metadata as Record<string, unknown>).alignment;
  return isCharacterAlignment(alignment) ? alignment : null;
}

interface AudioPreviewProps {
  audioUrl: string;
  alignment?: CharacterAlignmentResponseModel | null;
  className?: string;
}

/**
 * Audio preview for workflow results.
 *
 * When character-level alignment is available (from TTS-with-timestamps),
 * renders the ElevenLabs Transcript Viewer with synced word highlighting.
 * Otherwise falls back to the simple audio player.
 */
export const AudioPreview: React.FC<AudioPreviewProps> = ({
  audioUrl,
  alignment = null,
  className,
}) => {
  if (alignment) {
    return (
      <TranscriptViewerContainer
        // Force a full remount on source change so the audio element,
        // its event listeners, and the RAF loop all start fresh instead
        // of leaving stale playback state around from the previous track.
        key={audioUrl}
        audioSrc={audioUrl}
        audioType='audio/mpeg'
        alignment={alignment}
        className={
          className ??
          'w-full max-w-4xl rounded-md border border-border bg-transparent text-foreground'
        }
      >
        <TranscriptViewerAudio />
        <TranscriptViewerWords className='text-base text-foreground' />
        <div className='flex items-center gap-3'>
          <TranscriptViewerPlayPauseButton />
          <TranscriptViewerScrubBar />
        </div>
      </TranscriptViewerContainer>
    );
  }

  return (
    <div className={className ?? 'w-fit max-w-4xl'}>
      <AudioPlayer audioDataUrl={audioUrl} audioBar={true} />
    </div>
  );
};
